import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Download, Users, UserPlus, UserMinus, Trash2, Tag, Plus } from 'lucide-react';
import api from '@/lib/api';
import Session from 'supertokens-web-js/recipe/session';
import { formatDate } from '@/lib/utils';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Pagination from '@/components/ui/Pagination';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

const SUGGESTED_TAGS = ['vegetables', 'flowers', 'herbs', 'organic', 'indoor', 'perennials', 'fruit', 'seeds'];

interface Subscriber {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  subscribed_at: string;
  unsubscribed_at: string | null;
  source: string | null;
  is_registered: boolean;
  tags?: string[];
}

interface SubscribersResponse {
  subscribers: Subscriber[];
  pagination: {
    page: number;
    total_pages: number;
    total: number;
    limit: number;
  };
}

interface Stats {
  active_count: string;
  inactive_count: string;
  new_last_7_days: string;
  new_last_30_days: string;
}

type FilterType = 'all' | 'active' | 'inactive';

function TagEditor({ subscriber, onUpdate }: { subscriber: Subscriber; onUpdate: (id: string, tags: string[]) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const toggleTag = async (tag: string) => {
    const current = subscriber.tags || [];
    const newTags = current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag];
    setSaving(true);
    try {
      await api.patch(`/admin/newsletter/subscribers/${subscriber.id}/tags`, { tags: newTags });
      onUpdate(subscriber.id, newTags);
    } catch { /* ignore */ }
    setSaving(false);
  };

  const tags = subscriber.tags || [];

  return (
    <div className="relative" ref={ref}>
      <div className="flex items-center gap-1 flex-wrap">
        {tags.map(tag => (
          <span key={tag} className="px-1.5 py-0.5 text-xs bg-green-50 text-green-700 rounded">{tag}</span>
        ))}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-0.5 text-gray-400 hover:text-green-600 transition-colors rounded"
          title="Edit tags"
        >
          {tags.length === 0 ? <Plus className="h-3.5 w-3.5" /> : <Tag className="h-3.5 w-3.5" />}
        </button>
      </div>
      {isOpen && (
        <div className="absolute z-10 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 min-w-48">
          <p className="text-xs text-gray-500 mb-1.5">Click to toggle tags{saving ? ' (saving...)' : ''}</p>
          <div className="flex flex-wrap gap-1">
            {SUGGESTED_TAGS.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                  tags.includes(tag)
                    ? 'bg-green-100 border-green-300 text-green-800'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function NewsletterSubscribers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0, limit: 25 });
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [isExporting, setIsExporting] = useState(false);

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentFilter = (searchParams.get('filter') || 'all') as FilterType;
  const currentTagFilter = searchParams.get('tag') || '';

  const handleTagFilter = (tag: string) => {
    if (currentTagFilter === tag) {
      searchParams.delete('tag');
    } else {
      searchParams.set('tag', tag);
    }
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const handleTagUpdate = (id: string, tags: string[]) => {
    setSubscribers(subs => subs.map(s => s.id === id ? { ...s, tags } : s));
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (debouncedSearch) {
      searchParams.set('q', debouncedSearch);
      searchParams.set('page', '1');
    } else {
      searchParams.delete('q');
    }
    setSearchParams(searchParams, { replace: true });
  }, [debouncedSearch]);

  // Fetch stats
  useEffect(() => {
    api.get<Stats>('/admin/newsletter/subscribers/stats')
      .then(setStats)
      .catch(() => {});
  }, []);

  // Fetch subscribers
  useEffect(() => {
    const fetchSubscribers = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('page', currentPage.toString());
        if (debouncedSearch) params.set('q', debouncedSearch);
        if (currentFilter !== 'all') params.set('filter', currentFilter);
        if (currentTagFilter) params.set('tag', currentTagFilter);

        const response = await api.get<SubscribersResponse>(
          `/admin/newsletter/subscribers?${params.toString()}`
        );
        setSubscribers(response.subscribers);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load subscribers');
      } finally {
        setIsLoading(false);
      }
    };
    fetchSubscribers();
  }, [currentPage, debouncedSearch, currentFilter, currentTagFilter]);

  const handlePageChange = (page: number) => {
    searchParams.set('page', page.toString());
    setSearchParams(searchParams);
  };

  const handleFilterChange = (filter: FilterType) => {
    searchParams.set('filter', filter);
    searchParams.set('page', '1');
    setSearchParams(searchParams);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';
      const token = await Session.doesSessionExist() ? await Session.getAccessToken() : null;
      const response = await fetch(`${apiUrl}/admin/newsletter/subscribers/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subscribers-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export subscribers');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRemoveSubscriber = async (id: string) => {
    if (!confirm('Remove this subscriber?')) return;
    try {
      await api.delete(`/admin/newsletter/subscribers/${id}`);
      setSubscribers(subs => subs.map(s => s.id === id ? { ...s, is_active: false } : s));
      if (stats) {
        setStats({
          ...stats,
          active_count: String(parseInt(stats.active_count) - 1),
          inactive_count: String(parseInt(stats.inactive_count) + 1),
        });
      }
    } catch {
      setError('Failed to remove subscriber');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Newsletter Subscribers</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage your email subscriber list
          </p>
        </div>
        <Button
          onClick={handleExport}
          disabled={isExporting}
          variant="secondary"
        >
          <Download className="h-4 w-4 mr-2" />
          {isExporting ? 'Exporting...' : 'Export CSV'}
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-xl font-bold text-gray-900">{parseInt(stats.active_count).toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <UserMinus className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Inactive</p>
                <p className="text-xl font-bold text-gray-900">{parseInt(stats.inactive_count).toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserPlus className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">New (7 days)</p>
                <p className="text-xl font-bold text-gray-900">{parseInt(stats.new_last_7_days).toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card padding="md">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserPlus className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">New (30 days)</p>
                <p className="text-xl font-bold text-gray-900">{parseInt(stats.new_last_30_days).toLocaleString()}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Search + Filters */}
      <Card padding="md">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search by email or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search subscribers"
            />
          </div>
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <div className="flex items-center gap-4 mt-3">
          <div className="flex gap-2">
            {(['all', 'active', 'inactive'] as FilterType[]).map((filter) => (
              <button
                key={filter}
                onClick={() => handleFilterChange(filter)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  currentFilter === filter
                    ? 'bg-green-100 text-green-800 font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
          <div className="h-5 w-px bg-gray-200" />
          <div className="flex gap-1.5 flex-wrap items-center">
            <Tag className="h-3.5 w-3.5 text-gray-400" />
            {SUGGESTED_TAGS.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagFilter(tag)}
                className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                  currentTagFilter === tag
                    ? 'bg-green-100 border-green-300 text-green-800'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        {error && (
          <div className="px-6 py-4 bg-red-50 border-b border-red-200 text-red-800" role="alert">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        ) : subscribers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {debouncedSearch ? 'No subscribers found matching your search' : 'No subscribers yet'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Tags
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Source
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Subscribed
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscribers.map((subscriber) => (
                    <tr key={subscriber.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{subscriber.email}</span>
                          {subscriber.is_registered && (
                            <Badge variant="blue">Registered</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {subscriber.first_name || subscriber.last_name
                          ? `${subscriber.first_name || ''} ${subscriber.last_name || ''}`.trim()
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={subscriber.is_active ? 'green' : 'gray'}>
                          {subscriber.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <TagEditor subscriber={subscriber} onUpdate={handleTagUpdate} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                        {subscriber.source || 'popup'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(subscriber.subscribed_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {subscriber.is_active && (
                          <button
                            onClick={() => handleRemoveSubscriber(subscriber.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded"
                            title="Unsubscribe"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.total_pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>
                    {' '}to{' '}
                    <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
                    {' '}of{' '}
                    <span className="font-medium">{pagination.total}</span> results
                  </p>
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.total_pages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
